import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Package, Truck, MapPin, CheckCircle, FileText, History } from 'lucide-react';
import { printInvoice } from '../utils/printInvoice';

const statusSteps = [
  { key: 'confirmed', label: 'Confirmed', icon: CheckCircle },
  { key: 'processing', label: 'Processing', icon: Package },
  { key: 'shipped', label: 'Shipped', icon: Truck },
  { key: 'delivered', label: 'Delivered', icon: MapPin },
];

const OrderTracking = () => {
  const [orderId, setOrderId] = useState('');
  const [email, setEmail] = useState('');
  const [order, setOrder] = useState(null);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!orderId.trim() || !email.trim()) {
      setError('Please enter both your order ID and email.');
      return;
    }
    setLoading(true);
    setError('');
    setOrder(null);
    setHistory([]);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders/history`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_id: orderId.trim(), email: email.trim() })
      });
      if (!res.ok) {
        const data = await res.json();
        setError(data.detail || 'Order not found.');
        setLoading(false);
        return;
      }
      const data = await res.json();
      const matched = data.find(o => o.order_id === orderId.trim().toUpperCase()) || data[0];
      setOrder(matched);
      setHistory(data);
    } catch {
      setError('Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  const getStatusIndex = (status) => {
    const idx = statusSteps.findIndex(s => s.key === status);
    return idx >= 0 ? idx : 0;
  };

  return (
    <div className="min-h-screen pt-32 pb-24">
      <div className="max-w-2xl mx-auto px-4 md:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4 text-center" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="track-order-heading">
            Track Your Order
          </h1>
          <p className="text-foreground/70 text-center mb-10">
            Enter your order number and email to check the status of your order.
          </p>

          <form onSubmit={handleTrack} className="p-8 rounded-2xl border border-border bg-muted/30 mb-10" data-testid="track-order-form">
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Order ID</label>
                <input
                  type="text"
                  value={orderId}
                  onChange={(e) => setOrderId(e.target.value)}
                  placeholder="e.g. RB-0001"
                  className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                  data-testid="track-order-id-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="john@example.com"
                  className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                  data-testid="track-order-email-input"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full px-8 py-4 bg-primary text-primary-foreground rounded-full hover:bg-primary/90 transition-all duration-300 font-semibold text-lg flex items-center justify-center space-x-2 disabled:opacity-50"
              data-testid="track-order-submit-button"
            >
              <Search className="w-5 h-5" />
              <span>{loading ? 'Searching...' : 'Track Order'}</span>
            </button>
            {error && (
              <p className="text-red-500 text-sm mt-4 text-center" data-testid="track-order-error">{error}</p>
            )}
          </form>

          {order && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="p-8 rounded-2xl border border-border bg-background/50" data-testid="track-order-result">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Order #{order.order_id}
                  </h2>
                  <p className="text-sm text-foreground/60 mt-1">
                    Placed {new Date(order.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                </div>
                <span className="px-4 py-1.5 rounded-full text-sm font-semibold bg-primary/10 text-primary capitalize" data-testid="order-status-badge">
                  {order.status}
                </span>
              </div>

              <button
                onClick={() => printInvoice(order)}
                className="w-full mb-6 px-4 py-3 rounded-xl border-2 border-primary text-primary hover:bg-primary/5 transition-colors font-semibold text-sm flex items-center justify-center space-x-2"
                data-testid="print-invoice-button"
              >
                <FileText className="w-4 h-4" />
                <span>Print / Save Invoice</span>
              </button>

              {/* Status Timeline */}
              <div className="mb-8">
                <div className="flex items-center justify-between relative">
                  <div className="absolute top-5 left-0 right-0 h-0.5 bg-border" />
                  <div className="absolute top-5 left-0 h-0.5 bg-primary transition-all duration-500" style={{ width: `${(getStatusIndex(order.status) / (statusSteps.length - 1)) * 100}%` }} />
                  {statusSteps.map((step, i) => {
                    const active = i <= getStatusIndex(order.status);
                    const Icon = step.icon;
                    return (
                      <div key={step.key} className="relative z-10 flex flex-col items-center" data-testid={`status-step-${step.key}`}>
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground border border-border'}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <span className={`text-xs mt-2 font-medium ${active ? 'text-primary' : 'text-muted-foreground'}`}>{step.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Tracking Number */}
              {order.tracking_number && (
                <div className="mb-6 p-4 rounded-xl bg-primary/5 border border-primary/20" data-testid="tracking-number-display">
                  <div className="flex items-center space-x-3">
                    <Truck className="w-6 h-6 text-primary flex-shrink-0" />
                    <div>
                      <p className="text-xs text-foreground/60 font-medium">Tracking Number</p>
                      <p className="text-lg font-bold text-foreground">{order.tracking_number}</p>
                    </div>
                    {order.carrier && (
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary ml-auto">{order.carrier}</span>
                    )}
                  </div>
                </div>
              )}

              {/* Order Items */}
              <div className="border-t border-border pt-6">
                <h3 className="text-lg font-semibold text-foreground mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Items</h3>
                <div className="space-y-3">
                  {order.items.map((item, i) => (
                    <div key={i} className="flex justify-between items-center py-2 text-sm">
                      <div>
                        <span className="font-medium text-foreground">{item.name}</span>
                        <span className="text-foreground/60 ml-2">({item.size}) x{item.quantity}</span>
                      </div>
                      <span className="font-semibold text-foreground">${(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Order Totals */}
              <div className="border-t border-border pt-4 mt-4 space-y-2">
                <div className="flex justify-between text-sm text-foreground/70">
                  <span>Subtotal</span>
                  <span>${order.subtotal.toFixed(2)}</span>
                </div>
                {order.discount > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Discount{order.promo_code ? ` (${order.promo_code})` : ''}</span>
                    <span>-${order.discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm text-foreground/70">
                  <span>Shipping</span>
                  <span>{order.shipping === 0 ? 'FREE' : `$${order.shipping.toFixed(2)}`}</span>
                </div>
                <div className="flex justify-between text-lg font-bold text-foreground pt-2 border-t border-border">
                  <span>Total</span>
                  <span>${order.total.toFixed(2)}</span>
                </div>
              </div>

              {/* Shipping Info */}
              {order.shipping_address && (
                <div className="border-t border-border pt-4 mt-4">
                  <h3 className="text-sm font-semibold text-foreground mb-2">{order.shipping_method === 'pickup' ? 'Pickup Location' : 'Shipping To'}</h3>
                  <p className="text-sm text-foreground/70">
                    {order.shipping_address.pickup_location
                      ? order.shipping_address.pickup_location
                      : <>
                          {order.customer_name}<br />
                          {order.shipping_address.street}{order.shipping_address.street2 ? <><br />{order.shipping_address.street2}</> : null}<br />
                          {order.shipping_address.suburb} {order.shipping_address.state} {order.shipping_address.postcode}
                        </>}
                  </p>
                </div>
              )}
            </motion.div>
          )}

          {/* Order History */}
          {history.length > 1 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }} className="mt-8" data-testid="order-history-section">
              <div className="flex items-center space-x-2 mb-4">
                <History className="w-5 h-5 text-primary" />
                <h2 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>Your Order History</h2>
              </div>
              <div className="space-y-3">
                {history.map(h => (
                  <button
                    key={h.order_id}
                    onClick={() => { setOrder(h); window.scrollTo({ top: 200, behavior: 'smooth' }); }}
                    className={`w-full flex items-center justify-between p-4 rounded-xl border text-left transition-colors ${h.order_id === order?.order_id ? 'border-primary bg-primary/5' : 'border-border bg-background/50 hover:border-primary/50'}`}
                    data-testid={`history-order-${h.order_id}`}
                  >
                    <div>
                      <p className="font-semibold text-foreground text-sm">Order #{h.order_id}</p>
                      <p className="text-xs text-foreground/60 mt-0.5">
                        {new Date(h.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}
                        {' · '}{h.items.reduce((sum, i) => sum + i.quantity, 0)} item{h.items.reduce((sum, i) => sum + i.quantity, 0) !== 1 ? 's' : ''}
                      </p>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="font-bold text-foreground text-sm">${h.total.toFixed(2)}</span>
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary capitalize">{h.status}</span>
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default OrderTracking;
